import { SimpleGrid } from '@chakra-ui/react';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { t } from '@lingui/core/macro';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';

import useDashboardContext from '../../context/useDashboardContext';
import buildLinkToEnrolments from '../../utils/buildLinkToEnrolments';
import ErrorFallback from '../ErrorFallback';
import CardGroup from './shared/CardGroup';
import ProgressCard from './shared/ProgressCard';
import Section from './shared/Section';
import { StudentEnrolmentHealth } from '@duke-hq/svc-api-db';

type Props = {
  data?: RouterOutputs['dashboard']['getSemesterProgress'];
};

const healthFilters = [
  StudentEnrolmentHealth.BEHIND,
  StudentEnrolmentHealth.SLIPPING,
  StudentEnrolmentHealth.NOT_STARTED,
  StudentEnrolmentHealth.ON_TRACK,
];

function SemesterProgressUI({ data }: Props) {
  const { notStarted, oneQuarter, halfWay, threeQuarter, complete } = data ?? {};

  return (
    <SimpleGrid columns={{ base: 1, lg: 5 }} spacing="8px">
      <ProgressCard
        to={buildLinkToEnrolments({
          health: StudentEnrolmentHealth.NOT_STARTED,
        })}
        backgroundColour="purple.300"
        label={t`Not started`}
        value={notStarted}
      />
      <ProgressCard
        to={buildLinkToEnrolments({
          percent: 'l25',
          health: [
            StudentEnrolmentHealth.SLIPPING,
            StudentEnrolmentHealth.BEHIND,
            StudentEnrolmentHealth.ON_TRACK,
          ],
        })}
        backgroundColour="purple.400"
        label={t`< 25%`}
        value={oneQuarter}
      />
      <ProgressCard
        to={buildLinkToEnrolments({ percent: '25-75', health: healthFilters })}
        backgroundColour="purple.500"
        label={t`25 - 74%`}
        value={halfWay}
      />
      <ProgressCard
        to={buildLinkToEnrolments({ percent: 'g75', health: healthFilters })}
        backgroundColour="purple.600"
        label={t`> 75%`}
        value={threeQuarter}
      />
      <ProgressCard
        to={buildLinkToEnrolments({ percent: '100' })}
        backgroundColour="purple.700"
        label={t`Complete`}
        value={complete}
      />
    </SimpleGrid>
  );
}

function SemesterProgressWithData() {
  const { filters } = useFiltersContext();
  const { data } = trpcTenant.dashboard.getSemesterProgress.useQuery(
    { filters },
    {
      suspense: true,
      trpc: {
        context: {
          skipBatch: true,
        },
      },
    },
  );

  if (!data || Object.keys(data).length === 0) {
    return <ErrorFallback />;
  }

  return <SemesterProgressUI data={data} />;
}

export default function Progress() {
  const { openPanel } = useDashboardContext();

  return (
    <Section title={t`Progress`} tooltipText={t`Percentage of current semester completed`}>
      <CardGroup
        cardType="muted"
        title={t`Semester progress`}
        openPanel={() => openPanel('SemesterProgress')}
      >
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Suspense fallback={<SemesterProgressUI />}>
            <SemesterProgressWithData />
          </Suspense>
        </ErrorBoundary>
      </CardGroup>
    </Section>
  );
}
